import Alpine from 'alpinejs';

Alpine.data('pps', () => ({
  isEnabled: false,
  registryOpen: true,

  baseParentLink: '',

  error: null,
  warning: null,
  success: null,

  ppsSettings: null,
  loading: false,
  copied: false,
  copiedTimeout: null,

  activeVariants: [],
  selectedVariants: [],

  // Levy state
  hasLevyAccess: false,
  levyEnabled: false,
  levyEnabledOriginal: false,
  levyLoading: false,
  levyStats: { total: 0, count: 0 } as { total: number; count: number },

  init: async function () {
    this.baseParentLink = this.$el.dataset.ppsLink;
    this.registryOpen = this.$el.dataset.registryOpen === 'true';
    this.hasLevyAccess = this.$el.dataset.hasLevyAccess === 'true';
    this.fetchParentPaymentStatus();
    this.attachUnsavedChangesAlert();
    this.initTooltips();

    this.$refs.subjectsForm.addEventListener('submit', this.handleSubjectsFormSubmit.bind(this));

    // If parent payment is enabled but there is 0 active variants, show a warning
    this.$watch('activeVariants,isEnabled', (value) => {
      if (this.isEnabled && value.length === 0) {
        this.setMessage(
          'warning',
          "Parent payment system is enabled but there are no subject selected. Please select subjects and ensure you click 'Update Subjects' to confirm your selection.",
          false,
          true,
        );
      }
    });
  },

  attachUnsavedChangesAlert: function () {
    window.addEventListener('beforeunload', (event) => {
      // Only show confirmation dialog if there are pending changes
      if (this.getPendingCount() > 0) {
        const message = 'You have unsaved subject changes. Are you sure you want to leave?';
        event.preventDefault();
        event.returnValue = message; // For older browsers
        return message; // For modern browsers
      }
    });
  },

  initTooltips: function () {
    // Initialize Bootstrap tooltips after a short delay to ensure DOM is ready
    this.$nextTick(() => {
      if (typeof jQuery !== 'undefined' && jQuery.fn.tooltip) {
        jQuery(this.$el).find('[data-toggle="tooltip"]').tooltip();
      }
    });
  },

  getParentPageLink() {
    if (!this.ppsSettings) {
      return '';
    }

    return new URL(`/pages/parent-payment/${this.ppsSettings.pps_code}`, this.baseParentLink);
  },

  showConfirmation() {
    (this.$refs.confirmation as HTMLDialogElement).showModal();
  },

  async copyDetails() {
    const text = `Parent Payment System code: ${this.ppsSettings.pps_code}\nLink to buy: ${this.getParentPageLink()}`;

    try {
      await navigator.clipboard.writeText(text);
    } catch (error) {
      console.error('Could not copy parent payment details:', error);
      return;
    }

    clearTimeout(this.copiedTimeout);
    this.copied = true;
    this.copiedTimeout = setTimeout(() => {
      this.copied = false;
    }, 3000);
  },

  fetchParentPaymentStatus: async function () {
    this.loading = true;
    this.clearMessages();

    try {
      const response = await fetch('/apps/acidgreen/parent-payment/status');
      const data = await response.json();

      this.ppsSettings = data;

      this.isEnabled = data.enabled;
      this.activeVariants = data.variants?.map((variant) => variant.id.replace(/\D/g, '')) || [];
      this.selectedVariants = this.activeVariants;

      // Set levy state from backend
      this.levyEnabled = data.levy_enabled || false;
      this.levyEnabledOriginal = this.levyEnabled;
      this.levyStats = data.levy_stats || { total: 0, count: 0 };

      // Re-initialize tooltips after data loads and elements become visible
      this.initTooltips();
    } catch (error) {
      console.error('Error fetching parent payment status:', error);
      this.setMessage('error', 'Could not fetch parent payment system status.');
    } finally {
      this.loading = false;
    }
  },

  clearMessages: function () {
    this.error = null;
    this.warning = null;
    this.success = null;
  },

  setMessage: function (
    type: 'error' | 'warning' | 'success',
    message: string,
    shouldClear = true,
    shouldScrollTo = false,
  ) {
    if (shouldClear) {
      this.clearMessages();
    }

    this[type] = message;

    if (shouldScrollTo) {
      this.$refs.notifications.scrollIntoView({ behavior: 'smooth' });
    }
  },

  async togglePaymentSystem() {
    if (this.loading) {
      return;
    }

    try {
      this.loading = true;
      this.clearMessages();

      const url = `/apps/acidgreen/parent-payment/status`;

      const response = await fetch(url, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          enabled: !this.isEnabled,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      this.isEnabled = data.enabled;

      if (this.ppsSettings) {
        this.ppsSettings.enabled = this.isEnabled;
        this.ppsSettings.pps_code = data.pps_code;
      }

      if (this.isEnabled && this.ppsSettings) {
        this.showConfirmation();
      }
    } catch (error) {
      this.setMessage('error', 'An error occurred while updating the parent payment system status.', true, true);
      console.error('An error occurred:', error);
    } finally {
      this.loading = false;
    }
  },

  handleInput() {
    const inputs = this.$refs.variants.querySelectorAll('input[data-variant]');

    this.selectedVariants = Array.from(inputs)
      .filter((input: HTMLInputElement) => input.checked)
      .map((input: HTMLInputElement) => input.dataset.variant);
  },

  getPendingCount() {
    let count = 0;

    // Count variants that are active but not selected (will be removed)
    for (const variantId of this.activeVariants) {
      if (!this.selectedVariants.includes(variantId)) {
        count++;
      }
    }

    // Count variants that are selected but not active (will be added)
    for (const variantId of this.selectedVariants) {
      if (!this.activeVariants.includes(variantId)) {
        count++;
      }
    }

    return count;
  },

  isPending(variantId) {
    if (this.activeVariants.includes(variantId) && !this.selectedVariants.includes(variantId)) {
      return true;
    }

    if (!this.activeVariants.includes(variantId) && this.selectedVariants.includes(variantId)) {
      return true;
    }

    return false;
  },

  handleSubjectsFormSubmit: function (event) {
    event.preventDefault();
    this.updateEnabledVariants();
  },

  async updateEnabledVariants() {
    if (this.getPendingCount() <= 0) {
      return;
    }

    if (this.loading) {
      return;
    }

    const payload = {
      variants: this.selectedVariants,
    };

    try {
      this.loading = true;
      this.clearMessages();

      const url = `/apps/acidgreen/parent-payment/variants`;

      const response = await fetch(url, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      this.activeVariants = data.variants;

      // The pop-up tells schools parents can now buy, which is only true while PPS is enabled.
      if (this.isEnabled) {
        this.showConfirmation();
      }
    } catch (error) {
      console.error('An error occurred:', error);
      this.setMessage('error', 'An error occurred while updating the enabled variants.', true, true);
    } finally {
      this.loading = false;
    }
  },

  hasLevyChanges() {
    return this.levyEnabled !== this.levyEnabledOriginal;
  },

  async saveLevySettings() {
    if (!this.hasLevyChanges() || this.levyLoading) {
      return;
    }

    try {
      this.levyLoading = true;
      this.clearMessages();

      const url = `/apps/acidgreen/parent-payment/levy`;

      const response = await fetch(url, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          levy_enabled: this.levyEnabled,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      this.levyEnabled = data.levy_enabled;
      this.levyEnabledOriginal = data.levy_enabled;

      this.setMessage('success', 'School support fee settings updated successfully.', true, true);
    } catch (error) {
      console.error('An error occurred:', error);
      this.setMessage('error', 'An error occurred while updating the school support fee settings.', true, true);
    } finally {
      this.levyLoading = false;
    }
  },

  formatMoney(cents: number): string {
    return '$' + (cents / 100).toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,');
  },
}));
